import { Button, Checkbox, InputNumber, Select, Tag, Tooltip, Typography } from "antd";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import { Table } from "antd";
import {
  computeDrinkRowCost,
  computeDrinksEffectiveTotal,
  computeDrinksTotalByType,
  computeRecipePrice,
} from "../calculations";
import type { DrinkItem, DrinkRecipe } from "../types";
import type { FestivalCalcStore } from "../store";
import { AutocompleteInput } from "./shared/AutocompleteInput";
import { SectionWrapper } from "./shared/SectionWrapper";
import { TagPriceEditor } from "./shared/TagPriceEditor";
import { UserBadge } from "./shared/UserBadge";
import { RecipeModal } from "./RecipeModal";

interface Props {
  store: FestivalCalcStore;
}

export const DrinksSection = observer(({ store }: Props) => {
  const { drinkItems, tags, activeTagId, segments, config } = store;
  const [recipeModal, setRecipeModal] = useState<{ drink: DrinkItem; recipe: DrinkRecipe } | null>(null);

  const allNames = drinkItems.map((i) => i.name).filter(Boolean);
  const allTypes = drinkItems.map((i) => i.drinkType).filter(Boolean);
  const recipeIdsKey = drinkItems.map((d) => d.recipe?.objectId).filter(Boolean).join(",");

  React.useEffect(() => {
    const ids = recipeIdsKey ? recipeIdsKey.split(",") : [];
    ids.forEach((id) => store.loadIngredientsForRecipe(id));
  }, [recipeIdsKey, store]);
  
  const ingredientsByDrink = new Map(
    drinkItems
      .filter((d) => d.recipe?.objectId)
      .map((d) => [d.objectId!, store.ingredientsByRecipe.get(d.recipe!.objectId!) ?? []])
  );

  const costByType = computeDrinksTotalByType(
    drinkItems,
    ingredientsByDrink,
    activeTagId,
    tags,
    segments,
    config.zusagenPercent
  );

  const { effectiveByType, total } = computeDrinksEffectiveTotal(
    costByType,
    config.drinkTypeMinimums
  );

  const drinkTypes = Array.from(
    new Set(drinkItems.map((d) => d.drinkType).filter(Boolean))
  );

  const segmentOptions = segments.map((s) => ({ value: s.id, label: `${s.name} (${s.attendeeCount})` }));

  async function openRecipe(item: DrinkItem) {
    let recipe = item.recipe;
    if (!recipe) {
      recipe = await store.addRecipe(item.name || "Neues Rezept");
      item.objectId && await store.updateDrinkItem(item.objectId, { recipe });
    }
    if (recipe?.objectId) {
      await store.loadIngredientsForRecipe(recipe.objectId);
    }
    setRecipeModal({ drink: item, recipe });
  }

  const columns = [
    {
      title: "Aktiv",
      width: 55,
      render: (_: any, item: DrinkItem) => (
        <Checkbox
          checked={item.active}
          onChange={(e) =>
            item.objectId && store.updateDrinkItem(item.objectId, { active: e.target.checked })
          }
        />
      ),
    },
    {
      title: "Item",
      render: (_: any, item: DrinkItem) => (
        <AutocompleteInput
          value={item.name}
          onChange={(v) => item.objectId && store.updateDrinkItem(item.objectId, { name: v })}
          suggestions={allNames}
          placeholder="Item"
        />
      ),
    },
    {
      title: "Typ",
      render: (_: any, item: DrinkItem) => (
        <AutocompleteInput
          value={item.drinkType}
          onChange={(v) => item.objectId && store.updateDrinkItem(item.objectId, { drinkType: v })}
          suggestions={allTypes}
          placeholder="Typ"
        />
      ),
    },
    {
      title: "Vol. (ml)",
      width: 90,
      render: (_: any, item: DrinkItem) => (
        <InputNumber
          size="small"
          min={1}
          step={1}
          value={item.volumeMl}
          onChange={(v) =>
            item.objectId && store.updateDrinkItem(item.objectId, { volumeMl: Math.max(1, v ?? 1) })
          }
          style={{ width: 80 }}
        />
      ),
    },
    {
      title: "Rezept",
      width: 80,
      render: (_: any, item: DrinkItem) => {
        const ingredients = item.recipe?.objectId
          ? (store.ingredientsByRecipe.get(item.recipe.objectId) ?? [])
          : [];
        const hasActiveIngredients = ingredients.some((i) => i.active);
        return (
          <Tooltip title={item.recipe ? item.recipe.name : "Rezept erstellen / bearbeiten"}>
            <Button
              size="small"
              type={hasActiveIngredients ? "primary" : "default"}
              onClick={() => openRecipe(item)}
            >
              ✎
            </Button>
          </Tooltip>
        );
      },
    },
    {
      title: "Preis",
      render: (_: any, item: DrinkItem) => {
        const ingredients = item.recipe?.objectId
          ? (store.ingredientsByRecipe.get(item.recipe.objectId) ?? [])
          : [];
        const recipePrice = computeRecipePrice(ingredients);
        const { resolved } = computeDrinkRowCost(
          item,
          activeTagId,
          tags,
          segments,
          ingredients,
          config.zusagenPercent
        );

        if (recipePrice !== null) {
          return (
            <Tooltip title="Preis aus Rezept berechnet">
              <Tag color="blue">{recipePrice.toFixed(2)} €</Tag>
            </Tooltip>
          );
        }

        return (
          <TagPriceEditor
            tagPrices={item.tagPrices}
            allTags={tags}
            priceField="manualPrice"
            resolved={resolved}
            onChange={(updated) =>
              item.objectId && store.updateDrinkItem(item.objectId, { tagPrices: updated as any })
            }
          />
        );
      },
    },
    {
      title: "p. Kopf",
      width: 80,
      render: (_: any, item: DrinkItem) => (
        <InputNumber
          size="small"
          min={1}
          step={1}
          value={item.servingsPerPerson}
          onChange={(v) =>
            item.objectId && store.updateDrinkItem(item.objectId, { servingsPerPerson: Math.max(1, v ?? 1) })
          }
          style={{ width: 70 }}
        />
      ),
    },
    {
      title: "Tage",
      render: (_: any, item: DrinkItem) =>
        segmentOptions.length > 0 ? (
          <Select
            mode="multiple"
            size="small"
            value={item.eventSegmentIds}
            options={segmentOptions}
            onChange={(v) =>
              item.objectId && store.updateDrinkItem(item.objectId, { eventSegmentIds: v })
            }
            placeholder="Tage"
            style={{ minWidth: 160 }}
          />
        ) : (
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>Keine Segmente</Typography.Text>
        ),
    },
    {
      title: "Summe",
      width: 100,
      render: (_: any, item: DrinkItem) => {
        const ingredients = item.recipe?.objectId
          ? (store.ingredientsByRecipe.get(item.recipe.objectId) ?? [])
          : [];
        const { cost } = computeDrinkRowCost(
          item,
          activeTagId,
          tags,
          segments,
          ingredients,
          config.zusagenPercent
        );
        return <Typography.Text>{cost.toFixed(2)} €</Typography.Text>;
      },
    },
    {
      title: "Ist-Kosten",
      key: "actualCost",
      width: 160,
      render: (_: any, item: DrinkItem) => {
        const ingredients = item.recipe?.objectId
          ? (store.ingredientsByRecipe.get(item.recipe.objectId) ?? [])
          : [];
        const { cost } = computeDrinkRowCost(
          item,
          activeTagId,
          tags,
          segments,
          ingredients,
          config.zusagenPercent
        );
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Checkbox
              checked={item.isActual}
              onChange={(e) => {
                const patch: Partial<DrinkItem> = { isActual: e.target.checked };
                if (e.target.checked && item.actualCost === null) patch.actualCost = cost;
                item.objectId && store.updateDrinkItem(item.objectId, patch);
              }}
            />
            {item.isActual && (
              <InputNumber
                size="small"
                min={0}
                step={0.01}
                value={item.actualCost ?? 0}
                onChange={(v) =>
                  item.objectId && store.updateDrinkItem(item.objectId, { actualCost: v ?? 0 })
                }
                style={{ width: 95 }}
                addonAfter="€"
              />
            )}
          </div>
        );
      },
    },
    {
      title: "",
      width: 40,
      render: (_: any, item: DrinkItem) => <UserBadge user={item.createdBy} />,
    },
    {
      title: "",
      width: 40,
      render: (_: any, item: DrinkItem) => (
        <Button
          type="text"
          danger
          size="small"
          onClick={() => item.objectId && store.deleteDrinkItem(item.objectId)}
        >
          ✖
        </Button>
      ),
    },
  ];

  return (
    <>
      <SectionWrapper title="Getränke" total={total} onAdd={() => store.addDrinkItem()}>
        <Table
          dataSource={drinkItems}
          columns={columns}
          rowKey={(r) => r.objectId ?? Math.random().toString()}
          pagination={false}
          size="small"
          scroll={{ x: "max-content" }}
        />
        {drinkTypes.length > 0 && (
          <div style={{ marginTop: 12, borderTop: "1px solid #f0f0f0", paddingTop: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ color: "#888" }}>
                  <th style={{ textAlign: "left", padding: "2px 8px", fontWeight: 500 }}>Typ</th>
                  <th style={{ textAlign: "right", padding: "2px 8px", fontWeight: 500 }}>Berechnet</th>
                  <th style={{ textAlign: "right", padding: "2px 8px", fontWeight: 500 }}>Mindestbetrag</th>
                  <th style={{ textAlign: "right", padding: "2px 8px", fontWeight: 500 }}>Effektiv</th>
                </tr>
              </thead>
              <tbody>
                {drinkTypes.map((type) => {
                  const calculated = costByType[type] ?? 0;
                  const minimum = config.drinkTypeMinimums[type.replaceAll("$", "").replaceAll(".", "")] ?? 0;
                  const effective = effectiveByType[type] ?? 0;
                  const isMinimumActive = effective > calculated;
                  return (
                    <tr key={type}>
                      <td style={{ padding: "2px 8px" }}>{type}</td>
                      <td style={{ textAlign: "right", padding: "2px 8px", color: "#888" }}>
                        {calculated.toFixed(2)} €
                      </td>
                      <td style={{ textAlign: "right", padding: "2px 8px" }}>
                        <InputNumber
                          size="small"
                          min={0}
                          step={1}
                          value={minimum}
                          onChange={(v) => store.updateDrinkTypeMinimum(type, v ?? 0)}
                          style={{ width: 110 }}
                          addonAfter="€"
                        />
                      </td>
                      <td style={{ textAlign: "right", padding: "2px 8px" }}>
                        <Typography.Text strong={isMinimumActive} type={isMinimumActive ? "warning" : undefined}>
                          {effective.toFixed(2)} €
                        </Typography.Text>
                      </td>
                    </tr>
                  );
                })}
                <tr style={{ borderTop: "1px solid #f0f0f0" }}>
                  <td style={{ padding: "4px 8px", fontWeight: 600 }} colSpan={3}>Gesamt</td>
                  <td style={{ textAlign: "right", padding: "4px 8px", fontWeight: 600 }}>
                    {total.toFixed(2)} €
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </SectionWrapper>

      <RecipeModal
        open={!!recipeModal}
        recipe={recipeModal?.recipe ?? null}
        store={store}
        onClose={() => setRecipeModal(null)}
      />
    </>
  );
});
